import React, { useState, useEffect } from 'react';
import { Alert, Button, Form, Modal, Badge } from 'react-bootstrap';
import { DatePicker } from "../datePicker";
import SearchDropdown from '../Settings/TaskManagementSettings/SearchDropdown';
import { displayToastMsg } from '../toastMessages/toastmessages';

interface EditTaskModalProps {
    showEditModal: boolean;
    setShowEditModal: (show: boolean) => void;
    taskToEdit: any;
    taskDate: string;
    setTaskDate: (date: string) => void;
    selectedUsers: string[];
    validationError: string | null;
    setValidationError: (error: string | null) => void;
    handleUserToggle: (userId: string) => void;
    handleSaveTask: (updatedDescription?: string) => void;
    handleDeleteTask: () => void;
    handleRemoveTask: (task: any) => void;
    // Delete modal props
    showDeleteModal: boolean;
    setShowDeleteModal: (show: boolean) => void;
    
    // Additional data
    allLawyers: any[];
    myTasks?: any[];
}

const EditTaskModal: React.FC<EditTaskModalProps> = ({
    showEditModal,
    setShowEditModal,
    taskToEdit,
    taskDate,
    setTaskDate,
    selectedUsers,
    validationError,
    setValidationError,
    handleUserToggle,
    handleSaveTask,
    handleDeleteTask,
    handleRemoveTask,
    showDeleteModal,
    setShowDeleteModal,
    allLawyers,
    myTasks = []
}) => {
    // State for editable task description
    const [taskDescription, setTaskDescription] = useState<string>("");
    
    // Initialize task description when modal opens or task changes
    useEffect(() => {
        if (taskToEdit && showEditModal) {
            setTaskDescription(getDisplayTitle());
        }
    }, [taskToEdit, showEditModal]);

    // Function to extract the display title from the full title
    const getDisplayTitle = () => {
        const fullTitle = taskToEdit?.title || '';
        const secondBracketRegex = /.*\(([^)]*)\)(?!.*\([^)]*\))/;
        const matches = fullTitle.match(secondBracketRegex);
        return matches ? matches[1] : fullTitle;
    };

    // Check if task is editable (custom task with typeID > 999999)
    const isTaskEditable = () => {
        return taskToEdit && taskToEdit.typeID > 999999;
    };

    // Parse task title for duplicate checking (same as AddInfileTaskModal)
    const parseTaskTitle = (fullTitle: string): string => {
        const nestedParenthesesRegex = /\(([^()]*\([^()]*\)[^()]*)\)/;
        const nestedMatches = fullTitle.match(nestedParenthesesRegex);
        
        if (nestedMatches) {
            return nestedMatches[1];
        } else {
            const secondBracketRegex = /.*\(([^)]*)\)(?!.*\([^)]*\))/;
            const matches = fullTitle.match(secondBracketRegex);
            return matches ? matches[1] : fullTitle;
        }
    };

    // Create a wrapper function to handle the save and show toast
    const handleSaveWithToast = () => {
        // Check validation before trying to save
        if (selectedUsers.length === 0) {
            return;
        }

        // Additional validation for editable tasks
        if (isTaskEditable()) {
            if (!taskDescription.trim()) {
                setValidationError("Task description is required");
                return;
            }

            // Check for duplicate task names if description changed
            const originalDescription = getDisplayTitle();
            if (taskDescription.trim() !== originalDescription) {
                const newTaskTitleParsed = parseTaskTitle(`(${taskDescription.trim()})`);
                const existingTaskTitles = myTasks
                    .filter(task => 
                        task.isActivated === 1 && 
                        task.typeID !== taskToEdit.typeID // Exclude current task
                    )
                    .map(task => parseTaskTitle(task.title))
                    .filter(title => title);
                
                const isDuplicate = existingTaskTitles.some(existingTitle => 
                    existingTitle.toLowerCase() === newTaskTitleParsed.toLowerCase()
                );
                
                if (isDuplicate) {
                    setValidationError("Cannot use duplicate task names");
                    return;
                }
            }
        }
        
        // Pass updated description to handleSaveTask if task is editable and description changed
        const originalDescription = getDisplayTitle();
        const updatedDescription = isTaskEditable() && taskDescription.trim() !== originalDescription 
            ? taskDescription.trim() 
            : undefined;
            
        handleSaveTask(updatedDescription);
        
        // Display a success toast message
        displayToastMsg({
            id: Date.now(),
            type: "success",
            msg: "Task Updated Successfully"
        }, null);
    };

    const handleRemoveWithToast = (task) => {
        handleRemoveTask(task);
        // Display a success toast for deletion
        displayToastMsg({
            id: Date.now(),
            type: "success",
            msg: "Task Removed Successfully"
        }, null);
    };

    return (
        <>
            {/* Edit Task Modal */}
            <Modal size="sm" className="modal-sm-long" centered show={showEditModal} onHide={() => {
                setValidationError(null);
                setShowEditModal(false);
            }}>
                <Modal.Header closeButton={false}>
                    <div className="d-flex justify-content-between w-100 align-items-center">
                        <h2>
                            {(taskToEdit?.overwriteDate === 1 || taskToEdit?.overwritePerson === 1) ? (
                                <>
                                    Task <small style={{ color: 'var(--txt-40)' }}><i>(edited)</i></small>
                                </>
                            ) : (
                                'Edit Task'
                            )}
                        </h2>
                        {taskToEdit && taskToEdit.typeID > 1000 && (
                            <Badge bg="success">User</Badge>
                        )}
                    </div>
                </Modal.Header>
                <Modal.Body>
                    {validationError && (
                        <Alert variant="danger" className="mb-3">
                            {validationError}
                        </Alert>
                    )}
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Task Description</Form.Label>
                            <Form.Control 
                                type="text" 
                                value={isTaskEditable() ? taskDescription : getDisplayTitle()}
                                onChange={isTaskEditable() ? (e) => setTaskDescription(e.target.value) : undefined}
                                readOnly={!isTaskEditable()}
                                placeholder={isTaskEditable() ? "Enter task description" : undefined}
                            />
                            {isTaskEditable() && (
                                <div className="text-muted small mt-1">
                                    <i className="bi bi-pencil me-1"></i>
                                    This is a custom task and can be edited
                                </div>
                            )}
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Assigned Users</Form.Label>
                            <SearchDropdown
                                allValues={
                                    allLawyers.filter(user => !user.LSUCID).map(user => ({
                                        Name: user.Name,
                                        Id: user.SubUserId?.toString() || user.UserID?.toString()
                                    }))
                                }
                                selectedValues={selectedUsers}
                                onValueChange={handleUserToggle}
                                fullValuesList={allLawyers.map(user => ({
                                    Name: user.Name,
                                    Id: user.SubUserId?.toString() || user.UserID?.toString(),
                                    LSUCID: user.LSUCID
                                }))}
                            />
                            {selectedUsers.length === 0 && (
                                <div className="text-danger small mt-1">At least one user must be assigned</div>
                            )}
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Task Date</Form.Label>
                            <DatePicker
                                date={taskDate}
                                setDate={(newDate) => setTaskDate(newDate)}
                                placeholder="Select date"
                            />
                        </Form.Group>
                    </Form>
                    <Button variant="link" className="me-auto text-danger" onClick={handleDeleteTask}>
                        <i className="fa-regular fa-trash-can status-danger"></i> Remove task from file
                    </Button>
                </Modal.Body>

                <Modal.Footer className="justify-content-between">
                    <Button variant="text" onClick={() => {
                        setValidationError(null);
                        setShowEditModal(false);
                    }}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleSaveWithToast}>
                        Update
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Delete Task Confirmation Modal */}
            <Modal centered size="sm" show={showDeleteModal && showEditModal} onHide={() => setShowDeleteModal(false)}>
                <Modal.Header closeButton>
                    <h2>Remove Task</h2>
                </Modal.Header>
                <Modal.Body>
                    <div className="alert alert-danger">
                        Are you sure you want to remove this task?
                    </div>
                    <p>Once removed, this task will be deactivated from the file.</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={() => setShowDeleteModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={() => handleRemoveWithToast(taskToEdit)}>
                        Delete
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default EditTaskModal; 